import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ESTIMATE_CHOICES, formatMinutes } from './choreEffort';

function ChoreCreateModal({ chore, categories, rooms = [], deadlinePresets, onSave, onClose }) {
  const { t, i18n } = useTranslation();

  const roomName = (room) => (i18n.language === 'no' ? room.nameNo : room.nameEn) || room.nameEn;

  const priorityOptions = [
    { value: 0, label: `${t('chores.priority.low')} ⚪` },
    { value: 1, label: `${t('chores.priority.medium')} 🟡` },
    { value: 2, label: `${t('chores.priority.high')} 🔴` }
  ];

  const [title, setTitle] = useState(chore?.title || '');
  const [description, setDescription] = useState(chore?.description || '');
  const [categoryId, setCategoryId] = useState(chore?.categoryId || categories[0]?.id || '');
  const [priority, setPriority] = useState(chore?.priority ?? 1);
  const [roomId, setRoomId] = useState(chore?.roomId || '');
  const [estimatedMinutes, setEstimatedMinutes] = useState(chore?.estimatedMinutes ?? '');
  const [deadlinePresetId, setDeadlinePresetId] = useState(chore?.deadlinePresetId || '');
  const [dueDate, setDueDate] = useState(chore?.dueDate ? chore.dueDate.split('T')[0] : '');
  const [checklistItems, setChecklistItems] = useState([]);
  const [newItem, setNewItem] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !categoryId) return;

    const data = {
      title: title.trim(),
      description: description.trim() || null,
      categoryId,
      roomId: roomId || null,
      priority,
      estimatedMinutes: estimatedMinutes === '' ? null : parseInt(estimatedMinutes, 10),
      deadlinePresetId: deadlinePresetId || null,
      dueDate: dueDate || null,
      checklistItems: checklistItems.length > 0 ? checklistItems : null
    };

    onSave(data);
  };

  const addChecklistItem = () => {
    if (newItem.trim()) {
      setChecklistItems([...checklistItems, newItem.trim()]);
      setNewItem('');
    }
  };

  const removeChecklistItem = (index) => {
    setChecklistItems(checklistItems.filter((_, i) => i !== index));
  };

  return (
    <div className="chore-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label={chore ? t('chores.createModal.editChore') : t('chores.createModal.newChore')}>
      <div className="chore-modal" onClick={(e) => e.stopPropagation()}>
        <div className="chore-modal-header">
          <h3>{chore ? `✏️ ${t('chores.createModal.editChore')}` : `➕ ${t('chores.createModal.newChore')}`}</h3>
          <button className="chore-modal-close" onClick={onClose} aria-label="Close dialog">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="chore-modal-form">
          <div className="chore-form-group">
            <label>{t('chores.createModal.titleLabel')}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('chores.createModal.titlePlaceholder')}
              required
              autoFocus
              className="chore-input"
              aria-required="true"
            />
          </div>

          <div className="chore-form-group">
            <label>{t('chores.createModal.description')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('chores.createModal.descriptionPlaceholder')}
              className="chore-textarea"
              rows={2}
            />
          </div>

          <div className="chore-form-row">
            <div className="chore-form-group">
              <label>{t('chores.createModal.category')}</label>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="chore-select" required aria-required="true">
                <option value="" disabled>{t('chores.createModal.selectCategory')}</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.icon} {cat.nameEn}</option>
                ))}
              </select>
            </div>
            <div className="chore-form-group">
              <label>{t('chores.createModal.priority')}</label>
              <select value={priority} onChange={(e) => setPriority(parseInt(e.target.value))} className="chore-select">
                {priorityOptions.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="chore-form-row">
            <div className="chore-form-group">
              <label>{t('chores.createModal.room')}</label>
              <select value={roomId} onChange={(e) => setRoomId(e.target.value)} className="chore-select">
                <option value="">{t('chores.createModal.noRoom')}</option>
                {rooms.map(room => (
                  <option key={room.id} value={room.id}>{room.icon} {roomName(room)}</option>
                ))}
              </select>
            </div>
            <div className="chore-form-group">
              <label>{t('chores.createModal.estimate')}</label>
              <select
                value={estimatedMinutes}
                onChange={(e) => setEstimatedMinutes(e.target.value)}
                className="chore-select"
              >
                <option value="">{t('chores.effort.noEstimate')}</option>
                {ESTIMATE_CHOICES.map(minutes => (
                  <option key={minutes} value={minutes}>{formatMinutes(minutes, t)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="chore-form-row">
            <div className="chore-form-group">
              <label>{t('chores.createModal.deadlinePreset')}</label>
              <select
                value={deadlinePresetId}
                onChange={(e) => { setDeadlinePresetId(e.target.value); if (e.target.value) setDueDate(''); }}
                className="chore-select"
              >
                <option value="">{t('chores.createModal.noDeadline')}</option>
                {deadlinePresets.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="chore-form-group">
              <label>{t('chores.createModal.specificDate')}</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => { setDueDate(e.target.value); if (e.target.value) setDeadlinePresetId(''); }}
                className="chore-input"
              />
            </div>
          </div>

          {!chore && (
            <div className="chore-form-group">
              <label>{t('chores.createModal.checklistItems')}</label>
              <div className="chore-checklist-builder">
                {checklistItems.map((item, idx) => (
                  <div key={idx} className="chore-checklist-builder-item">
                    <span>☐ {item}</span>
                    <button type="button" className="chore-btn chore-btn-danger chore-btn-tiny" onClick={() => removeChecklistItem(idx)}>✕</button>
                  </div>
                ))}
                <div className="chore-add-item">
                  <input
                    type="text"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addChecklistItem(); } }}
                    placeholder={t('chores.createModal.addSubTask')}
                    className="chore-add-item-input"
                  />
                  <button type="button" className="chore-btn chore-btn-small" onClick={addChecklistItem} disabled={!newItem.trim()}>+</button>
                </div>
              </div>
            </div>
          )}

          <div className="chore-modal-actions">
            <button type="button" className="chore-btn chore-btn-secondary" onClick={onClose}>{t('chores.createModal.cancel')}</button>
            <button type="submit" className="chore-btn chore-btn-primary" disabled={!title.trim() || !categoryId}>
              {chore ? t('chores.createModal.saveChanges') : t('chores.createModal.createChore')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ChoreCreateModal;
